import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BUSINESS_TYPES,
  BUSINESS_TYPE_LABELS,
  CURRENCIES,
  CURRENCY_CODES,
  MODULES,
  MODULE_KEYS,
  resolveEnabledModules,
  type BusinessSettings,
  type BusinessType,
  type ModuleOverrides,
  DEFAULT_SCALE_CONFIG,
} from '@tengesa/shared';
import { api } from '../../lib/api';
import { CONFIGURABLE_METHODS, PAYMENT_METHODS, type ConfigurableMethod, type PaymentSettings } from '@tengesa/shared';
import { useTillCurrencies } from '../pos/PaymentDialog';
import { Button, Checkbox, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, Switch, TextArea, TextInput, fieldErrors, useToast } from '../../ui';

export function SettingsPage() {
  const q = useQuery({ queryKey: ['business'], queryFn: () => api.get<BusinessSettings>('/api/business') });
  return (
    <>
      <PageHeader title="Business settings" description="Your registered details, tax setup, currencies and the features your business uses." />
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data && <SettingsForm initial={q.data} />}
    </>
  );
}

type FormState = Omit<BusinessSettings, 'id' | 'enabledModules'> & { vatRatePercent: string };

function toForm(s: BusinessSettings): FormState {
  return { ...s, vatRatePercent: (s.standardVatRateBp / 100).toFixed(2).replace(/\.?0+$/, '') };
}

function SettingsForm({ initial }: { initial: BusinessSettings }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [s, setS] = useState<FormState>(() => toForm(initial));
  useEffect(() => setS(toForm(initial)), [initial]);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setS((p) => ({ ...p, [k]: v }));

  const vatBp = Math.round(Number(s.vatRatePercent) * 100);
  const save = useMutation({
    mutationFn: () => {
      const { vatRatePercent: _v, ...rest } = s;
      return api.put<BusinessSettings>('/api/business', { ...rest, standardVatRateBp: Number.isFinite(vatBp) ? vatBp : -1 });
    },
    onSuccess: (data) => {
      qc.setQueryData(['business'], data);
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      toast('Settings saved');
    },
  });
  const f = fieldErrors(save.error);
  const typeChanged = s.businessType !== initial.businessType;
  const enabled = resolveEnabledModules(s.businessType, s.moduleOverrides);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-6 pb-24">
      <FormError error={save.error} />

      <Panel title="Business details">
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Registered name" htmlFor="name" error={f.name} hint="As registered with ZIMRA">
            <TextInput id="name" value={s.name} onChange={(e) => set('name', e.target.value)} invalid={!!f.name} />
          </Field>
          <Field label="Trading name (optional)" htmlFor="trading" error={f.tradingName} hint="Shown on receipts and screens">
            <TextInput id="trading" value={s.tradingName ?? ''} onChange={(e) => set('tradingName', e.target.value)} />
          </Field>
          <Field label="Address" htmlFor="address" error={f.address} className="sm:col-span-2">
            <TextInput id="address" value={s.address ?? ''} onChange={(e) => set('address', e.target.value)} />
          </Field>
          <Field label="Phone" htmlFor="phone" error={f.phone}>
            <TextInput id="phone" type="tel" value={s.phone ?? ''} onChange={(e) => set('phone', e.target.value)} invalid={!!f.phone} />
          </Field>
          <Field label="Email" htmlFor="email" error={f.email}>
            <TextInput id="email" type="email" value={s.email ?? ''} onChange={(e) => set('email', e.target.value)} invalid={!!f.email} />
          </Field>
        </div>
      </Panel>

      <Panel title="Tax">
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="ZIMRA TIN" htmlFor="tin" error={f.tin} hint="10 digits">
            <TextInput id="tin" inputMode="numeric" value={s.tin ?? ''} onChange={(e) => set('tin', e.target.value.replace(/\D/g, ''))} maxLength={10} invalid={!!f.tin} />
          </Field>
          <div className="flex items-center justify-between gap-4 self-end rounded-[8px] bg-paper px-4 py-2.5">
            <span className="font-medium">Registered for VAT</span>
            <Switch checked={s.vatRegistered} onChange={(v) => set('vatRegistered', v)} label="Registered for VAT" />
          </div>
          {s.vatRegistered && (
            <>
              <Field label="VAT number" htmlFor="vat" error={f.vatNumber} hint="9 digits">
                <TextInput id="vat" inputMode="numeric" value={s.vatNumber ?? ''} onChange={(e) => set('vatNumber', e.target.value.replace(/\D/g, ''))} maxLength={9} invalid={!!f.vatNumber} />
              </Field>
              <Field label="Standard VAT rate (%)" htmlFor="vatrate" error={f.standardVatRateBp} hint="15.5% from 1 January 2026. Check with ZIMRA if the rate changes.">
                <TextInput id="vatrate" inputMode="decimal" value={s.vatRatePercent} onChange={(e) => set('vatRatePercent', e.target.value)} invalid={!!f.standardVatRateBp} />
              </Field>
              <p className="text-sm text-muted sm:col-span-2">
                VAT-registered businesses must fiscalise sales through ZIMRA's FDMS. That connection is set up in phase 5 and needs ZIMRA approval.
              </p>
            </>
          )}
        </div>
      </Panel>

      <Panel title="Currencies">
        <div className="grid gap-5 p-5 sm:grid-cols-[1fr_1.4fr]">
          <Field label="Base currency" htmlFor="base" error={f.baseCurrency} hint="Reports and profit are worked out in this currency">
            <Select
              id="base"
              value={s.baseCurrency}
              onChange={(e) => {
                const v = e.target.value as FormState['baseCurrency'];
                setS((p) => ({ ...p, baseCurrency: v, acceptedCurrencies: p.acceptedCurrencies.includes(v) ? p.acceptedCurrencies : [v, ...p.acceptedCurrencies] }));
              }}
            >
              {CURRENCY_CODES.map((c) => (
                <option key={c} value={c}>
                  {c} — {CURRENCIES[c].name}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <p className="mb-2 text-sm font-medium">Currencies you accept</p>
            <div className="grid grid-cols-2 gap-2.5">
              {CURRENCY_CODES.map((c) => (
                <Checkbox
                  key={c}
                  label={`${c}`}
                  description={CURRENCIES[c].name}
                  checked={s.acceptedCurrencies.includes(c)}
                  disabled={c === s.baseCurrency}
                  onChange={(v) => set('acceptedCurrencies', v ? [...s.acceptedCurrencies, c] : s.acceptedCurrencies.filter((x) => x !== c))}
                />
              ))}
            </div>
            {f.acceptedCurrencies && <p className="mt-1 text-sm text-danger">{f.acceptedCurrencies}</p>}
          </div>
        </div>
      </Panel>

      <Panel title="Business type and features">
        <div className="p-5">
          <Field label="Business type" htmlFor="btype" hint="Decides which specialised features are available.">
            <Select id="btype" value={s.businessType} onChange={(e) => set('businessType', e.target.value as BusinessType)} className="max-w-xs">
              {BUSINESS_TYPES.map((t) => (
                <option key={t} value={t}>
                  {BUSINESS_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
          {typeChanged && (
            <p className="mt-3 rounded-[8px] bg-warn-soft px-3 py-2 text-sm text-warn">
              Changing from {BUSINESS_TYPE_LABELS[initial.businessType]} to {BUSINESS_TYPE_LABELS[s.businessType]} changes which features everyone sees.
            </p>
          )}
          <ul className="mt-5 divide-y divide-line rounded-[8px] border border-line">
            {MODULE_KEYS.map((k) => {
              const m = MODULES[k];
              const a = m.availability[s.businessType];
              if (a === 'unavailable') return null;
              const on = enabled.includes(k);
              return (
                <li key={k} className="flex items-start justify-between gap-4 px-4 py-3">
                  <div>
                    <p className="font-medium">
                      {m.label}
                    </p>
                    <p className="text-sm text-muted">{m.description}</p>
                  </div>
                  {a === 'always' ? (
                    <span className="shrink-0 pt-0.5 text-sm text-muted">Always on</span>
                  ) : (
                    <Switch
                      checked={on}
                      label={m.label}
                      onChange={(v) => set('moduleOverrides', { ...s.moduleOverrides, [k]: v } as ModuleOverrides)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </Panel>

      {enabled.includes('weighing') && (
        <Panel title="Scale labels">
          <div className="grid gap-4 p-5 sm:grid-cols-3">
            <p className="text-sm text-muted sm:col-span-3">
              Match these to how your shop scale prints its barcodes. A label looks like 21 01234 01255 7: a prefix, the product's PLU code,
              then the weight in grams or the price in cents. Give each weighed product its PLU on the Products page.
            </p>
            <Field label="Prefixes the scale uses" htmlFor="sc-pre" error={f['scaleConfig.prefixes'] ?? f.scaleConfig} hint="Two digits from 20 to 29, separated by commas">
              <TextInput
                id="sc-pre"
                value={(s.scaleConfig ?? DEFAULT_SCALE_CONFIG).prefixes.join(', ')}
                onChange={(e) => set('scaleConfig', { ...(s.scaleConfig ?? DEFAULT_SCALE_CONFIG), prefixes: e.target.value.split(/[\s,]+/).filter(Boolean) })}
              />
            </Field>
            <Field label="PLU length" htmlFor="sc-len" hint="Digits of product code after the prefix">
              <Select id="sc-len" value={(s.scaleConfig ?? DEFAULT_SCALE_CONFIG).pluLength} onChange={(e) => set('scaleConfig', { ...(s.scaleConfig ?? DEFAULT_SCALE_CONFIG), pluLength: Number(e.target.value) })}>
                {[4, 5, 6].map((n) => <option key={n} value={n}>{n} digits</option>)}
              </Select>
            </Field>
            <Field label="The value on the label is" htmlFor="sc-mode">
              <Select id="sc-mode" value={(s.scaleConfig ?? DEFAULT_SCALE_CONFIG).mode} onChange={(e) => set('scaleConfig', { ...(s.scaleConfig ?? DEFAULT_SCALE_CONFIG), mode: e.target.value as 'weight' | 'price' })}>
                <option value="weight">Weight in grams</option>
                <option value="price">Price in cents</option>
              </Select>
            </Field>
          </div>
        </Panel>
      )}

      <PaymentMethodsPanel />

      <Panel title="Stock">
        <div className="flex items-start justify-between gap-4 p-5">
          <div>
            <p className="font-medium">Allow selling below zero stock</p>
            <p className="max-w-2xl text-sm text-muted">
              Off: the till refuses to sell more than the system says is on the shelf. On: the sale goes through and the stock shows below zero until
              a delivery or stocktake corrects it. Leave off unless deliveries are often recorded late.
            </p>
          </div>
          <Switch checked={s.allowNegativeStock} onChange={(v) => set('allowNegativeStock', v)} label="Allow selling below zero stock" />
        </div>
      </Panel>

      <Panel title="Receipts">
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Header" htmlFor="rh" error={f.receiptHeader} hint="Printed above the items. Up to 300 characters.">
            <TextArea id="rh" rows={3} value={s.receiptHeader ?? ''} onChange={(e) => set('receiptHeader', e.target.value)} />
          </Field>
          <Field label="Footer" htmlFor="rf" error={f.receiptFooter} hint="Returns policy, thank-you message.">
            <TextArea id="rf" rows={3} value={s.receiptFooter ?? ''} onChange={(e) => set('receiptFooter', e.target.value)} />
          </Field>
        </div>
      </Panel>

      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-surface/95 backdrop-blur lg:left-[260px]">
        <div className="mx-auto flex max-w-6xl items-center justify-end gap-3 px-4 py-3 sm:px-6 lg:px-10">
          <Button onClick={() => setS(toForm(initial))}>Undo changes</Button>
          <Button variant="primary" type="submit" busy={save.isPending}>
            Save settings
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Which ways of paying the till offers, and the merchant code or account customers pay into. */
function PaymentMethodsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const till = useTillCurrencies();
  const [edit, setEdit] = useState<PaymentSettings | null>(null);
  const current: PaymentSettings = edit ?? till.data?.paymentSettings ?? {};
  const get = (m: ConfigurableMethod) => current[m] ?? { enabled: true, account: null };
  const save = useMutation({
    mutationFn: () => api.put<PaymentSettings>('/api/payment-settings', current),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['till-currencies'] });
      setEdit(null);
      toast('Payment methods saved');
    },
  });
  const change = (m: ConfigurableMethod, v: Partial<{ enabled: boolean; account: string | null }>) => setEdit({ ...current, [m]: { ...get(m), ...v } });
  return (
    <Panel
      title="Payment methods"
      actions={edit && <Button size="sm" variant="primary" busy={save.isPending} onClick={() => save.mutate()}>Save payment methods</Button>}
    >
      <FormError error={save.error} />
      <ul className="divide-y divide-line">
        {CONFIGURABLE_METHODS.map((m) => {
          const s = get(m);
          const needsAccount = PAYMENT_METHODS[m].kind === 'mobile' || PAYMENT_METHODS[m].kind === 'bank';
          return (
            <li key={m} className="grid items-center gap-3 px-5 py-3 sm:grid-cols-[1fr_280px_auto]">
              <div>
                <p className="font-medium">{PAYMENT_METHODS[m].label}</p>
                <p className="text-sm text-muted">
                  {PAYMENT_METHODS[m].needsReference ? 'The cashier records the transaction reference; a reference can only be used once.' : m === 'cash' ? 'Counted in the till at closing.' : 'Settled by your bank.'}
                </p>
              </div>
              {needsAccount ? (
                <TextInput
                  aria-label={`${PAYMENT_METHODS[m].label} merchant code or account`}
                  placeholder={PAYMENT_METHODS[m].kind === 'bank' ? 'Bank and account number' : 'Merchant code'}
                  value={s.account ?? ''}
                  disabled={!s.enabled}
                  onChange={(e) => change(m, { account: e.target.value })}
                />
              ) : (
                <span />
              )}
              <Switch checked={s.enabled} label={`Accept ${PAYMENT_METHODS[m].label}`} onChange={(v) => change(m, { enabled: v })} />
            </li>
          );
        })}
      </ul>
      <p className="border-t border-line px-5 py-3 text-sm text-muted">
        Payments are recorded with the reference from the customer's confirmation message. Confirming mobile money automatically needs a merchant API
        agreement with the provider; see the integration notes.
      </p>
    </Panel>
  );
}
